import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { Button, Loading } from "@carbon/react";
import axios from "../../axios-orders";
import {
  extractApiError,
  registerValidationSchema,
  taxIdFormatter,
} from "../../utils";
import { usePublicCountries } from "../../hooks/useCountries";
import { UserCompanyType } from "../../types";
import AuthLayout from "../../components/carbon/AuthLayout/AuthLayout";
import CarbonForm from "../../components/carbon/Form/Form";
import Complete from "./Complete/Complete";
import Success from "./Success/Success";
import Error from "./Error/Error";
import UserTypeSelector from "./sections/UserTypeSelector";
import CompanyInfoSection from "./sections/CompanyInfoSection";
import PersonalInfoSection from "./sections/PersonalInfoSection";
import CredentialsSection from "./sections/CredentialsSection";
import TextField from "../../components/carbon/Form/TextField";
import TermsSection from "./sections/TermsSection";
import { RegisterFormValues, registerDefaultValues, SignUpResponse } from "./types";
import "./register.scss";

/**
 * Self-service signup (`POST /auth/signup`), rebuilt on Carbon + the shared RHF
 * `Controller`-bound field primitives and SPLIT into field-group sections
 * (`sections/*`) so no file exceeds ~400 lines. This parent orchestrates the form
 * (`useForm` + `yupResolver(registerValidationSchema)` + explicit `defaultValues`),
 * the BUYER/SELLER selector, the URL params, and the response → sub-screen flow.
 *
 * LAYOUT DECISION (this task's to make, per its brief): the pre-Carbon Register was
 * its own Tailwind `grid-cols-2` two-column shell — the form on the left, a
 * full-height `bg-spectum-ultraDark` panel with `icon-spectum.png` on the right.
 * Register renders through `AuthLayout` with `width="wide"` (42rem — a bigger,
 * multi-section form) and `brandPanel`, which restores that two-column split (the
 * form card on the left, the brand panel on the right, dropped below tablet). (An
 * earlier pass had used the plain centered card; the user asked for the split back.)
 *
 * SIGNUP CONTRACT — preserved exactly: `POST /auth/signup` with the optional
 * `?invitationId` query param and the `userType`/`origin` params; the seller/invited
 * role paths are BE-decided; buyer-without-invitation stays `BLOCKED` (backend — no
 * client logic bypasses it). On the response, `signUpResponse` is set and the flow
 * routes to `Success`/`Complete`/`Error` as before.
 */
export default function Register() {
  const { t } = useTranslation();

  const [userType, setUserType] = useState("SELLER");
  const [isLoading, setIsLoading] = useState(false);
  // Honeypot: a hidden field real users never fill; a bot that fills every input
  // trips it and the submit is silently dropped. Unchanged from the pre-Carbon form.
  const [hidden, setHidden] = useState("");
  const [signUpResponse, setSignUpResponse] = useState<SignUpResponse>({
    status: 0,
    data: { firstName: "", lastName: "" },
  });

  const search = useLocation().search;
  const isUrlParams = new URLSearchParams(search).get("userType");
  const invitation = new URLSearchParams(search).get("invitationId");
  const origin = new URLSearchParams(search).get("origin");
  // The sign-up link in an invitation email carries its code.
  const invitationCode = new URLSearchParams(search).get("invitationCode");

  const {
    data: countries,
    isError: isCountriesError,
    refetch: refetchCountries,
  } = usePublicCountries();

  const { control, handleSubmit, reset, setError } =
    useForm<RegisterFormValues>({
      resolver: yupResolver(registerValidationSchema),
      defaultValues: {
        ...registerDefaultValues,
        invitationCode: invitationCode ?? "",
      },
    });

  const isBuyer = userType === UserCompanyType.BUYER;

  const onSubmit = (data: RegisterFormValues) => {
    if (hidden) return;
    setIsLoading(true);
    const url = invitation
      ? `/auth/signup?invitationId=${invitation}`
      : "/auth/signup";
    axios
      .post(url, {
        ...data,
        // Only a supplier can claim an invitation; what a buyer left typed is not sent.
        invitationCode: isBuyer ? "" : data.invitationCode,
        taxId: taxIdFormatter(data.taxId),
        type: userType,
        userCompanyType:
          userType === UserCompanyType.BUYER
            ? UserCompanyType.BUYER
            : UserCompanyType.ADMIN,
        ...(origin && { origin }),
      })
      .then((res) => {
        setSignUpResponse(res);
        setUserType("");
        reset();
      })
      .catch((err) => {
        if (extractApiError(err).code === "INVITATION_CODE_INVALID") {
          setError("invitationCode", {
            message: t("errors.INVITATION_CODE_INVALID"),
          });
          return;
        }
        setSignUpResponse(err);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (isUrlParams && signUpResponse.status !== 201) setUserType(isUrlParams);
  }, [isUrlParams, signUpResponse.status]);

  if (isLoading) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  const isRegistered = signUpResponse.status === 201;

  return (
    <AuthLayout width="wide" brandPanel>
      {signUpResponse.status === 0 ? <Complete /> : null}

      {isRegistered ? <Success signUpResponse={signUpResponse} /> : null}
      {signUpResponse.status !== 0 && !isRegistered ? <Error /> : null}

      {!isRegistered ? (
        <UserTypeSelector value={userType} onChange={setUserType} />
      ) : null}

      {!isRegistered ? (
        <CarbonForm
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="register__form"
          autoComplete="off"
        >
          <CompanyInfoSection
            control={control}
            countries={countries}
            isCountriesError={isCountriesError}
            onRetryCountries={() => refetchCountries()}
          />
          <PersonalInfoSection control={control} />
          {!isBuyer && (
            <TextField
              name="invitationCode"
              label={t("register.form.invitationCode")}
              optional
              helperText={t("register.form.invitationCodeHint")}
              control={control}
            />
          )}
          <CredentialsSection control={control} />
          <TermsSection control={control} userType={userType} />

          <Button type="submit" className="register__submit">
            {t("register.form.cta")}
          </Button>

          <p className="register__disclosure">
            {t("register.form.disclosure")}
          </p>

          {/* Honeypot — visually hidden, off the tab order and the a11y tree. */}
          <input
            className="register__honeypot"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={hidden}
            onChange={(e) => setHidden(e.target.value)}
          />
        </CarbonForm>
      ) : null}
    </AuthLayout>
  );
}
